import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, Shield, Cookie, Scale, Plus, Trash2, Save, RotateCcw,
  ChevronDown, ChevronUp, Edit2, Eye, ExternalLink, Check, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

const ICON_OPTIONS = [
  { value: 'Shield', label: 'Shield' },
  { value: 'Lock', label: 'Lock' },
  { value: 'Eye', label: 'Eye' },
  { value: 'Database', label: 'Database' },
  { value: 'Globe', label: 'Globe' },
  { value: 'Mail', label: 'Mail' },
  { value: 'UserCheck', label: 'User Check' },
  { value: 'Scale', label: 'Scale' },
  { value: 'FileText', label: 'File Text' },
  { value: 'Cookie', label: 'Cookie' },
  { value: 'AlertTriangle', label: 'Alert' },
  { value: 'Settings', label: 'Settings' },
];

const PAGE_CONFIGS = {
  'privacy-policy': {
    title: 'Privacy Policy',
    icon: Shield,
    color: 'green',
    url: '/privacy-policy',
    description: 'Explains how you collect, use, and protect user data'
  },
  'terms-of-use': {
    title: 'Terms of Use',
    icon: Scale,
    color: 'blue',
    url: '/terms-of-use',
    description: 'Legal agreement governing use of your website'
  },
  'cookie-policy': {
    title: 'Cookie Policy',
    icon: Cookie,
    color: 'amber',
    url: '/cookies',
    description: 'Details about cookies and tracking technologies'
  }
};

const LegalPagesTab = ({ token }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [expandedSections, setExpandedSections] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchPages = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await api.get('/admin/legal-pages');
      setPages(res.data.pages || []);
    } catch (error) {
      console.error('Error fetching legal pages:', error);
      toast.error('Failed to load legal pages');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchPageDetails = useCallback(async (pageType) => {
    if (!token) return;
    
    try {
      const res = await api.get(`/admin/legal-pages/${pageType}`);
      setEditingPage(res.data.page);
      setSelectedPage(pageType);
    } catch (error) {
      console.error('Error fetching page details:', error);
      toast.error('Failed to load page details');
    }
  }, [token]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const handleSave = async () => {
    if (!editingPage || !token) return;
    
    setSaving(true);
    try {
      await api.put(`/admin/legal-pages/${selectedPage}`, {
        title: editingPage.title,
        last_updated: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        effective_date: editingPage.effective_date,
        sections: editingPage.sections,
        metadata: editingPage.metadata
      });
      
      toast.success('Page saved successfully');
      fetchPages();
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedPage || !token) return;
    
    if (!window.confirm('Are you sure you want to reset this page to default content? All custom changes will be lost.')) {
      return;
    }
    
    try {
      await api.post(`/admin/legal-pages/${selectedPage}/reset`);
      toast.success('Page reset to default');
      setSelectedPage(null);
      setEditingPage(null);
      fetchPages();
    } catch (error) {
      console.error('Error resetting page:', error);
      toast.error('Failed to reset page');
    }
  };

  const updateSection = (sectionIndex, field, value) => {
    setEditingPage(prev => ({
      ...prev,
      sections: prev.sections.map((s, i) => 
        i === sectionIndex ? { ...s, [field]: value } : s
      )
    }));
  };

  const addSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      title: 'New Section',
      content: 'Enter section content here...',
      icon: 'FileText'
    };
    
    setEditingPage(prev => ({
      ...prev,
      sections: [...(prev.sections || []), newSection]
    }));
    
    setExpandedSections(prev => [...prev, newSection.id]);
  };

  const removeSection = (sectionIndex) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;
    
    setEditingPage(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== sectionIndex)
    }));
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const colorClasses = {
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="legal-pages-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Legal Pages</h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            Manage your Privacy Policy, Terms of Use, and Cookie Policy
          </p>
        </div>
      </div>

      {!selectedPage ? (
        // Page Selection Grid
        <div className="grid md:grid-cols-3 gap-6">
          {Object.entries(PAGE_CONFIGS).map(([pageType, config]) => {
            const Icon = config.icon;
            const pageData = pages.find(p => p.page_type === pageType);
            
            return (
              <div
                key={pageType}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-xl ${colorClasses[config.color]}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  {pageData?.is_default && (
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full">
                      Default
                    </span>
                  )}
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {config.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {config.description}
                </p>
                
                {pageData && !pageData.is_default && (
                  <p className="text-xs text-gray-400 mb-4">
                    Last updated: {pageData.last_updated}
                  </p>
                )}
                
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchPageDetails(pageType)}
                    className="flex-1 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Edit2 className="h-4 w-4" />
                    Edit
                  </button>
                  <a
                    href={config.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    View
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // Page Editor
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Editor Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setSelectedPage(null);
                  setEditingPage(null);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronDown className="h-5 w-5 text-gray-500 rotate-90" />
              </button>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {PAGE_CONFIGS[selectedPage]?.title}
                </h3>
                <p className="text-xs text-gray-500">
                  {editingPage?.sections?.length || 0} sections
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <a
                href={PAGE_CONFIGS[selectedPage]?.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white flex items-center gap-1"
              >
                <ExternalLink className="h-4 w-4" />
                Preview
              </a>
              <button
                onClick={handleReset}
                className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Page Metadata */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Page Title
                </label>
                <input
                  type="text"
                  value={editingPage?.title || ''}
                  onChange={(e) => setEditingPage(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Updated
                </label>
                <input
                  type="text"
                  value={editingPage?.last_updated || ''}
                  onChange={(e) => setEditingPage(prev => ({ ...prev, last_updated: e.target.value }))}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                  placeholder="e.g., April 9, 2026"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Effective Date
                </label>
                <input
                  type="text"
                  value={editingPage?.effective_date || ''}
                  onChange={(e) => setEditingPage(prev => ({ ...prev, effective_date: e.target.value }))}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                  placeholder="e.g., April 9, 2026"
                />
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-900 dark:text-white">Sections</h4>
              <button
                onClick={addSection}
                className="px-3 py-1.5 text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
              >
                <Plus className="h-4 w-4" />
                Add Section
              </button>
            </div>

            <div className="space-y-3">
              {(editingPage?.sections || []).map((section, idx) => {
                const isExpanded = expandedSections.includes(section.id);
                
                return (
                  <div
                    key={section.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                  >
                    {/* Section Header */}
                    <div 
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 cursor-pointer"
                      onClick={() => toggleSection(section.id)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 font-mono">#{idx + 1}</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {section.title}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSection(idx);
                          }}
                          className="p-1 text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {/* Section Content */}
                    {isExpanded && (
                      <div className="p-4 space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Section ID
                            </label>
                            <input
                              type="text"
                              value={section.id}
                              onChange={(e) => updateSection(idx, 'id', e.target.value)}
                              className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Section Title
                            </label>
                            <input
                              type="text"
                              value={section.title}
                              onChange={(e) => updateSection(idx, 'title', e.target.value)}
                              className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Icon (optional)
                          </label>
                          <select
                            value={section.icon || ''}
                            onChange={(e) => updateSection(idx, 'icon', e.target.value)}
                            className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                          >
                            <option value="">No icon</option>
                            {ICON_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Content
                          </label>
                          <textarea
                            value={section.content}
                            onChange={(e) => updateSection(idx, 'content', e.target.value)}
                            rows={10}
                            className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono"
                            placeholder="Enter section content here..."
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Use double line breaks for paragraphs. Format: (a), (b), (c) for sub-points.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {(!editingPage?.sections || editingPage.sections.length === 0) && (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No sections yet. Click "Add Section" to create your first section.</p>
                </div>
              )}
            </div>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-t border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium">Legal Compliance Note</p>
                <p className="mt-1">
                  These pages are pre-written by a legal professional. Modify with caution and consider 
                  consulting with a legal advisor before making significant changes to ensure compliance 
                  with Fiji law and international regulations.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalPagesTab;
